import {
  Button,
  cn,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  ScrollShadow,
} from '@heroui/react'
import { Dropdown } from '@heroui/react'
import {
  type TenantDto,
  useActivateTenant,
  useGetTenants,
  usePermanentDeleteTenant,
  useSuspendTenant,
} from '@qse/api'
import {
  type Column,
  DEFAULT_RESELLER_ID,
  ModelControlEnum,
  QSE_ENTERPRISE_ID,
  UserRoleEnum,
} from '@qse/constants'
import { useActiveModelStore, useUserStore } from '@qse/stores'
import {
  ErrorData,
  FilterDropdown,
  type FilterOption,
  LoadingData,
  StatusChip,
  TableComponent,
  toast,
} from '@qse/ui'
import { useIsMobile, useURLParams } from '@qse/utils'
import { useDebounce } from '@uidotdev/usehooks'
import { ChevronDownIcon, ChevronRightIcon, XIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const columns: Column[] = [
  { name: 'Tenant Name', uid: 'tenantName', sortable: true },
  { name: 'Type', uid: 'type' },
  { name: 'Status', uid: 'status', sortable: true },
  { name: 'Plan Name', uid: 'planName' },
  { name: 'Registration Date', uid: 'createdAt', sortable: true },
  { name: '', uid: 'actions' },
]

// Reseller IDs that are considered "default" (QSE-managed resellers)
const DEFAULT_RESELLER_IDS = new Set([DEFAULT_RESELLER_ID, QSE_ENTERPRISE_ID])

const isDefaultResellerTenant = (resellerId: string) => DEFAULT_RESELLER_IDS.has(resellerId)

// Filter options
const tenantTypeOptions: FilterOption[] = [
  { key: 'all', label: 'All Types' },
  { key: 'qse-retail', label: 'QSE Retail' },
  { key: 'qse-enterprise', label: 'QSE Enterprise' },
]

const statusOptions: FilterOption[] = [
  { key: 'all', label: 'All Status' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'pending_confirmation', label: 'Pending Confirmation' },
  { key: 'incomplete', label: 'Incomplete' },
]

interface TenantCardProps {
  tenant: TenantDto
  isSuperAdmin: boolean
  onView: (tenant: TenantDto) => void
  onEdit: (tenant: TenantDto) => void
  onSuspend: (id: string, tenantName: string) => void
  onActivate: (id: string, tenantName: string) => void
  onDelete: (tenant: TenantDto) => void
  onNavigateToUsers: (tenantId: string) => void
}

const TenantCard = ({
  tenant,
  isSuperAdmin,
  onView,
  onEdit,
  onSuspend,
  onActivate,
  onDelete,
  onNavigateToUsers,
}: TenantCardProps) => {
  const isQSEEnterprise = tenant.resellerId === QSE_ENTERPRISE_ID
  const isQSERetail = !isQSEEnterprise
  const isDefault = isDefaultResellerTenant(tenant.resellerId)
  // Delete option for: QSE Retail with incomplete/pending_confirmation status (immediate deletion)
  // OR suspended tenants (both QSE Enterprise and QSE Retail - two-phase deletion)
  const canDelete =
    (isQSERetail && (tenant.status === 'incomplete' || tenant.status === 'pending_confirmation')) ||
    tenant.status === 'suspended'

  const canNavigate = tenant.status === 'active' && (!isSuperAdmin || (isSuperAdmin && isDefault))

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (canNavigate) {
      onNavigateToUsers(tenant.id)
    }
  }

  return (
    <div
      className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4 shadow relative cursor-pointer hover:shadow-md transition-shadow"
      role="button"
      onDoubleClick={handleDoubleClick}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-base truncate" title={tenant.name}>
              {tenant.name}
            </p>
            {isDefault && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 shrink-0">
                QSE
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-[#AFAFAF]">ID: {tenant.id}</div>
          <div className="text-xs text-gray-500 dark:text-[#AFAFAF] mt-1">
            {isQSEEnterprise ? 'QSE Enterprise' : 'QSE Retail'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dropdown
            classNames={{
              content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
            }}
          >
            <DropdownTrigger>
              <Button isIconOnly size="sm" variant="light">
                <span className="text-2xl">⋮</span>
              </Button>
            </DropdownTrigger>
            <DropdownMenu>
              <DropdownItem key="view" onPress={() => onView(tenant)}>
                View
              </DropdownItem>
              {isQSEEnterprise ? (
                <>
                  <DropdownItem key="edit" onPress={() => onEdit(tenant)}>
                    Edit
                  </DropdownItem>
                </>
              ) : null}
              {tenant.status === 'active' ? (
                <DropdownItem key="suspend" onPress={() => onSuspend(tenant.id, tenant.name)}>
                  Suspend
                </DropdownItem>
              ) : null}
              {tenant.status === 'suspended' ? (
                <DropdownItem key="activate" onPress={() => onActivate(tenant.id, tenant.name)}>
                  Activate
                </DropdownItem>
              ) : null}
              {canDelete ? (
                <DropdownItem key="delete" onPress={() => onDelete(tenant)}>
                  Delete
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
          {canNavigate ? (
            <Button
              isIconOnly
              className="text-gray-500 hover:text-primary dark:text-white dark:hover:text-white dark:hover:bg-gray-700/50"
              size="sm"
              variant="light"
              onPress={() => onNavigateToUsers(tenant.id)}
            >
              <ChevronRightIcon className="w-4 h-4 dark:text-white dark:group-hover:text-white" />
            </Button>
          ) : (
            <div className="w-8 h-8" />
          )}
        </div>
      </div>
      <div className="flex justify-between text-xs mt-2">
        <StatusChip status={tenant.status} />
      </div>
    </div>
  )
}

const TenantManagement = () => {
  const { user } = useUserStore()
  const location = useLocation()
  const navigate = useNavigate()
  const isSuperAdmin = user?.userRole === UserRoleEnum.SUPER_ADMIN
  const isResellerAdmin = user?.userRole === UserRoleEnum.RESELLER_ADMIN
  const isAdminRole = isSuperAdmin || isResellerAdmin

  const isMobile = useIsMobile()

  // URL parameter management
  const {
    values: urlParams,
    updateParam,
    updateParams,
    clearParams,
  } = useURLParams({
    page: { key: 'page', defaultValue: 1 },
    rowsPerPage: { key: 'rowsPerPage', defaultValue: 10 },
    search: { key: 'search', defaultValue: '' },
    tenantTypeFilter: { key: 'filter', defaultValue: 'all' },
    statusFilter: { key: 'status', defaultValue: 'all' },
    sortBy: { key: 'sortBy', defaultValue: '' },
    sortDirection: { key: 'sortDirection', defaultValue: '' as 'asc' | 'desc' | '' },
  })

  const { page, rowsPerPage, search, tenantTypeFilter, statusFilter, sortBy, sortDirection } =
    urlParams
  const [sortTimestamp, setSortTimestamp] = useState<number>(Date.now())

  // Check if we should show the not found error state
  const showNotFoundError = location.state?.showNotFoundError
  const hasShownErrorToast = useRef(false)

  // Show toast notification when redirected due to 404 error
  useEffect(() => {
    if (showNotFoundError && !hasShownErrorToast.current) {
      hasShownErrorToast.current = true
      toast.error('Page not found', {
        description: "The page or resource you're looking for doesn't exist",
      })
    }
  }, [showNotFoundError])

  // Restore state from URL parameters when returning from tenant users page
  useEffect(() => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    const returnState = currentUrlParams.get('returnState')

    // Check if we have return state parameter (indicating we came back from users page)
    if (returnState) {
      try {
        // Decode the URL-encoded JSON
        const decodedState = JSON.parse(decodeURIComponent(returnState))

        // Build clean URL params from decoded state
        const cleanParams = new URLSearchParams()

        // Add non-default values to the clean params
        if (decodedState.page && decodedState.page !== 1) {
          cleanParams.set('page', String(decodedState.page))
        }
        if (decodedState.rowsPerPage && decodedState.rowsPerPage !== 10) {
          cleanParams.set('rowsPerPage', String(decodedState.rowsPerPage))
        }
        if (decodedState.search && decodedState.search !== '') {
          cleanParams.set('search', decodedState.search)
        }
        if (decodedState.tenantTypeFilter && decodedState.tenantTypeFilter !== 'all') {
          cleanParams.set('filter', decodedState.tenantTypeFilter)
        }
        if (decodedState.statusFilter && decodedState.statusFilter !== 'all') {
          cleanParams.set('status', decodedState.statusFilter)
        }
        if (decodedState.sortBy && decodedState.sortBy !== '') {
          cleanParams.set('sortBy', decodedState.sortBy)
        }
        if (decodedState.sortDirection && decodedState.sortDirection !== '') {
          cleanParams.set('sortDirection', decodedState.sortDirection)
        }

        // Navigate to clean URL (removes returnState and sets proper params)
        const newUrl = cleanParams.toString()
          ? `${window.location.pathname}?${cleanParams.toString()}`
          : window.location.pathname

        navigate(newUrl, { replace: true })
      } catch (_error) {
        // Failed to parse return state - silently ignore and use default state
      }
    }
  }, [navigate])

  const debouncedSearch = useDebounce(search, 500)

  const offset = useMemo(() => (page - 1) * rowsPerPage, [page, rowsPerPage])

  const filters = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: offset,
      search: debouncedSearch.toLocaleLowerCase() || undefined,
      status:
        statusFilter === 'all'
          ? undefined
          : (statusFilter as 'active' | 'suspended' | 'pending_confirmation' | 'incomplete') ||
            undefined,
      // Only apply sortBy if it exists
      sortBy: sortBy || undefined,
      sortOrder:
        sortBy && sortDirection ? (sortDirection.toUpperCase() as 'ASC' | 'DESC') : undefined,
      _sortTimestamp: sortTimestamp, // This ensures query key changes when sorting changes
    }),
    [rowsPerPage, offset, debouncedSearch, statusFilter, sortBy, sortDirection, sortTimestamp],
  )

  const resellerId = useMemo(() => {
    return tenantTypeFilter === 'qse-enterprise'
      ? QSE_ENTERPRISE_ID
      : tenantTypeFilter === 'qse-retail'
        ? DEFAULT_RESELLER_ID // We'll filter this client-side since we don't have a specific reseller ID for retail
        : undefined // All tenants - let server return all
  }, [tenantTypeFilter])

  const {
    data: tenantsData,
    isLoading,
    error,
  } = useGetTenants(resellerId, filters, {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
  })

  const { mutateAsync: mutateAsyncSuspendTenant } = useSuspendTenant()
  const { mutateAsync: mutateAsyncActivateTenant } = useActivateTenant()
  const { mutateAsync: mutateAsyncPermanentDeleteTenant } = usePermanentDeleteTenant()

  const handlePageChange = (newPage: number) => {
    updateParam('page', newPage, false)
  }

  const handleRowsPerPageChange = (newLimit: number) => {
    updateParam('rowsPerPage', newLimit)
  }

  // Handle sorting changes - cycles through asc → desc → none
  const handleSortChange = (columnKey: string, direction: 'asc' | 'desc') => {
    setSortTimestamp(Date.now()) // Update timestamp to force query refetch

    // Clear sorting if columnKey is empty or explicitly cleared
    if (columnKey === '' || !columnKey) {
      updateParams({
        sortBy: '',
        sortDirection: '',
        page: 1,
      })

      return
    }

    // Check current sort state for this column
    const isCurrentlySorted = sortBy === columnKey
    const currentlySortedDesc = isCurrentlySorted && sortDirection === 'desc'

    // If currently sorted DESC and trying to go to ASC, clear the sort instead
    // This intercepts HeroUI's natural cycle (asc → desc → asc) at the desc → asc transition
    if (currentlySortedDesc && direction === 'asc') {
      // Clear sort by setting to empty strings (which removes them from URL)
      updateParams({
        sortBy: '',
        sortDirection: '',
        page: 1,
      })
    } else {
      // Set or update the sort
      updateParams({
        sortBy: columnKey,
        sortDirection: direction,
        page: 1,
      })
    }
  }

  // Reset to first page when search or filters change
  const handleFilterChange = (newFilter: string) => {
    updateParam('tenantTypeFilter', newFilter)
  }

  const handleStatusFilterChange = (newStatusFilter: string) => {
    updateParam('statusFilter', newStatusFilter)
  }

  const handleSearchChange = (newSearch: string) => {
    updateParam('search', newSearch)
  }

  // Filter tenants by type (for retail tenants since we can't filter by reseller ID on server)
  const data = useMemo(() => {
    const allTenants = tenantsData?.data || []

    // If we're filtering for QSE Enterprise, the server already filtered by resellerId
    if (tenantTypeFilter === 'qse-enterprise' || tenantTypeFilter === 'all') {
      return allTenants
    }

    // For QSE Retail, filter client-side since we don't have a specific reseller ID
    if (tenantTypeFilter === 'qse-retail') {
      return allTenants.filter(tenant => tenant.resellerId !== QSE_ENTERPRISE_ID)
    }

    return allTenants
  }, [tenantsData?.data, tenantTypeFilter])

  // Calculate display count for header - this shows total results for current query/filter
  const displayCount = useMemo(() => {
    if (isLoading) return 0

    return tenantsData?.pageInfo.count || 0
  }, [tenantsData?.pageInfo, isLoading])

  const totalPages = tenantsData?.pageInfo.totalPages || 1

  const handleClearFilters = () => {
    clearParams()
  }

  const handleAddEditTenant = (tenant: TenantDto, action: string) => {
    const config = {
      modalHeader: {
        title: action === 'edit' ? 'Edit Tenant' : 'Create New Tenant',
        subtitle:
          action === 'edit'
            ? 'Please fill the form to edit the Tenant.'
            : 'Set up a new tenant with storage allocation and configuration settings..',
        showCloseButton: true,
      },
      data:
        action === 'edit'
          ? {
              tenant: tenant,
              resellerId: resellerId,
              filters: filters,
            }
          : undefined,
      action: action,
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[560px]',
      modelActive: ModelControlEnum.ADD_TENANT,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleViewTenant = (tenant: TenantDto) => {
    const config = {
      modalHeader: {
        title: 'View Tenant',
        subtitle: 'Tenant information and details',
        showCloseButton: true,
      },
      tenant: tenant,
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-2xl',
      modelActive: ModelControlEnum.VIEW_TENANT,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleSuspendTenant = (id: string, tenantName: string) => {
    const config = {
      modalHeader: {
        title: 'Suspend Tenant',
        showCloseButton: true,
      },
      title: 'Suspend Tenant Access?',
      message: `Are you sure you want to suspend access for ${tenantName}? This will prevent users from logging in and accessing the system.`,
      icon: '/assets/svg/warning.svg',
      actions: [
        {
          label: 'Cancel',
          color: 'default',
          variant: 'flat',
        },
        {
          label: 'Suspend Tenant',
          color: 'warning',
          onPress: async () => {
            await mutateAsyncSuspendTenant(id)
          },
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[660px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
    })
  }

  const handleActivateTenant = (id: string, tenantName: string) => {
    const config = {
      modalHeader: {
        title: 'Activate Tenant',
        showCloseButton: true,
      },
      title: 'Activate Tenant Access?',
      message: `Are you sure you want to activate access for ${tenantName}? This will restore full functionality for all users.`,
      icon: '/assets/svg/active.svg',
      actions: [
        {
          label: 'Cancel',
          color: 'default',
          variant: 'flat',
        },
        {
          label: 'Activate Tenant',
          color: 'success',
          onPress: async () => {
            await mutateAsyncActivateTenant(id)
          },
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[660px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
    })
  }

  const handlePermanentDeleteTenant = (tenant: TenantDto) => {
    const isQSEEnterprise = tenant.resellerId === QSE_ENTERPRISE_ID
    const isQSERetail = !isQSEEnterprise
    const isSuspended = tenant.status === 'suspended'
    const isImmediateDelete =
      isQSERetail && (tenant.status === 'incomplete' || tenant.status === 'pending_confirmation')

    // For suspended tenants (both QSE Enterprise and QSE Retail), use the two-phase deletion modal
    if (isSuspended) {
      const config = {
        modalHeader: {
          title: '⚠️ Delete Tenant - Permanent Action',
          showCloseButton: true,
        },
        data: {
          tenant: {
            id: tenant.id,
            name: tenant.name,
            resellerId: tenant.resellerId,
            status: tenant.status,
          },
          // Note: onSuccess removed - mutation already invalidates queries which triggers automatic refetch
        },
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[600px]',
        modelActive: ModelControlEnum.DELETE_TENANT_CONFIRMATION,
        toggleModel: true,
        scrollBehavior: 'outside',
      })
    }
    // For QSE Retail tenants in incomplete/pending_confirmation status, use immediate deletion modal
    else if (isImmediateDelete) {
      const config = {
        modalHeader: {
          title: 'Delete Tenant',
          showCloseButton: true,
        },
        title: 'Delete Tenant',
        message:
          'This will permanently delete the Tenant. Deletion begins immediately but may take some time to fully complete. This action cannot be undone.',
        icon: '/assets/svg/delete.svg',
        actions: [
          {
            label: 'Cancel',
            color: 'default',
            variant: 'flat',
          },
          {
            label: 'Delete',
            color: 'danger',
            onPress: async () => {
              await mutateAsyncPermanentDeleteTenant({ id: tenant.id, skipRetention: true })
            },
          },
        ],
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[660px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        toggleModel: true,
      })
    }
  }

  const handleNavigateToTenantUsers = (tenantId: string) => {
    // Encode all return state into a single parameter
    const returnState = {
      page,
      rowsPerPage,
      search,
      tenantTypeFilter,
      statusFilter,
      sortBy,
      sortDirection,
    }

    // URL encode the JSON string
    const encodedState = encodeURIComponent(JSON.stringify(returnState))

    // Navigate to tenant users page with encoded return state
    navigate(`/dashboard/management/tenant/${tenantId}/users?returnState=${encodedState}`)
  }

  const tableColumns = useMemo(() => {
    return isAdminRole ? columns : columns.filter(column => column.uid !== 'actions')
  }, [isAdminRole])

  const renderCell = (data: TenantDto, columnKey: Column['uid']) => {
    const tenant = data
    const isQSEEnterprise = tenant.resellerId === QSE_ENTERPRISE_ID
    const isQSERetail = !isQSEEnterprise
    const isDefault = isDefaultResellerTenant(tenant.resellerId)
    // Delete option for: QSE Retail with incomplete/pending_confirmation status (immediate deletion)
    // OR suspended tenants (both QSE Enterprise and QSE Retail - two-phase deletion)
    const canDelete =
      (isQSERetail &&
        (tenant.status === 'incomplete' || tenant.status === 'pending_confirmation')) ||
      tenant.status === 'suspended'

    const canNavigate = tenant.status === 'active' && (!isSuperAdmin || (isSuperAdmin && isDefault))

    switch (columnKey) {
      case 'tenantName':
        return (
          <div className="flex items-center gap-3 text-gray-900 dark:text-white min-w-0">
            <div className="flex flex-col gap-1 font-light text-sm min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-medium truncate" title={tenant.name}>
                  {tenant.name}
                </p>
                {isDefault && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 shrink-0">
                    QSE
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-[#AFAFAF]">ID: {tenant.id}</p>
            </div>
          </div>
        )
      case 'type':
        return (
          <span className="text-gray-900 dark:text-white text-sm">
            {isQSEEnterprise ? 'QSE Enterprise' : 'QSE Retail'}
          </span>
        )
      case 'status':
        return <StatusChip status={tenant.status} />
      case 'planName':
        return (
          <span className="text-gray-900 dark:text-white">
            {tenant.plan?.name ? tenant.plan.name : 'N/A'}
          </span>
        )
      case 'createdAt':
        return (
          <span className="text-gray-900 dark:text-white text-sm">
            {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'N/A'}
          </span>
        )
      case 'actions':
        return (
          <div className="relative flex justify-end items-center gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <span className="text-2xl">⋮</span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem key="view" onPress={() => handleViewTenant(tenant)}>
                  View
                </DropdownItem>
                {isQSEEnterprise ? (
                  <>
                    <DropdownItem key="edit" onPress={() => handleAddEditTenant(tenant, 'edit')}>
                      Edit
                    </DropdownItem>
                  </>
                ) : null}
                {tenant.status === 'active' ? (
                  <DropdownItem
                    key="suspend"
                    onPress={() => handleSuspendTenant(tenant.id, tenant.name)}
                  >
                    Suspend
                  </DropdownItem>
                ) : null}
                {tenant.status === 'suspended' ? (
                  <DropdownItem
                    key="activate"
                    onPress={() => handleActivateTenant(tenant.id, tenant.name)}
                  >
                    Activate
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <DropdownItem key="delete" onPress={() => handlePermanentDeleteTenant(tenant)}>
                    Delete
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </Dropdown>
            {canNavigate ? (
              <Button
                isIconOnly
                className="text-gray-500 hover:text-primary dark:text-white dark:hover:text-white dark:hover:bg-gray-700/50"
                size="sm"
                variant="light"
                onPress={() => handleNavigateToTenantUsers(tenant.id)}
              >
                <ChevronRightIcon className="w-4 h-4 dark:text-white dark:group-hover:text-white" />
              </Button>
            ) : (
              <div className="w-8 h-8" />
            )}
          </div>
        )

      default:
        return tenant[columnKey as keyof typeof tenant]
    }
  }

  return (
    <section className="md:px-[30px] px-4 flex flex-col gap-5 pb-24 md:pb-20 xl:pb-0">
      <div className="flex justify-between flex-wrap items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-medium text-2xl">
              Tenant Management{' '}
              {!isLoading && `(${displayCount} ${displayCount === 1 ? 'result' : 'results'})`}
            </h1>
          </div>
          <p>Manage your API keys and access tokens</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="max-w-[278px]">
            <Input
              isClearable
              maxLength={256}
              placeholder="Find anything"
              radius="sm"
              size="lg"
              value={search}
              variant="bordered"
              classNames={{
                label: [
                  'text-foreground',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-[#000]',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent w-[278px] !pr-32',
                  'dark:text-white text-black',
                  'placeholder:text-[#94A3B8] dark:placeholder:text-white/60 group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'dark:bg-[#1e1e20] group-data-[focus=true]:!border-[#000] dark:group-data-[focus=true]:!border-[#fff]',
                  'dark:hover:bg-[#1e1e20]/70',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  '!cursor-text',
                ],
              }}
              endContent={
                <div className="flex items-center">
                  <span
                    className={cn(
                      'text-xs text-gray-500 w-10',
                      search.length == 0 ? 'opacity-0' : 'opacity-100',
                    )}
                  >
                    {search.length}/256
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleSearchChange('')}
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              }
              startContent={
                <img alt="search" className="dark:invert" src="/assets/svg/search.svg" />
              }
              onClear={() => handleSearchChange('')}
              onValueChange={handleSearchChange}
            />
          </div>
          <FilterDropdown
            label="All Types"
            minWidth="min-w-[180px]"
            options={tenantTypeOptions}
            selectedKey={tenantTypeFilter}
            showClearButton={true}
            onSelectionChange={handleFilterChange}
          />
          <FilterDropdown
            label="All Status"
            minWidth="min-w-[180px]"
            options={statusOptions}
            selectedKey={statusFilter}
            showClearButton={true}
            onSelectionChange={handleStatusFilterChange}
          />
          <Button
            color="default"
            radius="sm"
            size="lg"
            variant="bordered"
            onPress={handleClearFilters}
          >
            Clear Filters
          </Button>
          {isAdminRole && (
            <Button
              className="md:min-w-[128px]"
              color="primary"
              radius="sm"
              size="lg"
              onPress={() => {
                handleAddEditTenant({} as TenantDto, 'create')
              }}
            >
              Create Enterprise Tenant
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <ErrorData
          description="An unexpected error occurred while loading tenants data."
          title="Error Loading Data"
        />
      ) : isLoading ? (
        <div className="max-h-[calc(100vh-250px)]">
          <LoadingData
            description="Please wait while we fetch your data..."
            title="Loading Tenants"
          />
        </div>
      ) : isMobile ? (
        <div className="">
          {data.length === 0 ? (
            <div className="text-center text-gray-600 dark:text-[#AFAFAF] py-8">
              <div className="text-lg font-medium mb-2">No tenants found</div>
              <div className="text-sm">
                {debouncedSearch || tenantTypeFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No tenants have been created yet'}
              </div>
            </div>
          ) : (
            <ScrollShadow
              hideScrollBar
              className="max-h-[calc(100vh-320px)] overflow-y-auto flex flex-col gap-4"
            >
              {data.map(tenant => (
                <TenantCard
                  key={tenant.id}
                  isSuperAdmin={isSuperAdmin}
                  tenant={tenant}
                  onActivate={handleActivateTenant}
                  onDelete={tenant => handlePermanentDeleteTenant(tenant)}
                  onEdit={tenant => handleAddEditTenant(tenant, 'edit')}
                  onNavigateToUsers={handleNavigateToTenantUsers}
                  onSuspend={handleSuspendTenant}
                  onView={handleViewTenant}
                />
              ))}
            </ScrollShadow>
          )}

          {data.length > 0 && (
            <div className="flex justify-between items-center mt-4 gap-2">
              <Dropdown
                size="sm"
                classNames={{
                  content:
                    'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
                }}
              >
                <DropdownTrigger>
                  <Button
                    endContent={<ChevronDownIcon className="text-small" />}
                    size="sm"
                    variant="light"
                  >
                    {rowsPerPage}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Rows per page"
                  selectedKeys={new Set([rowsPerPage.toString()])}
                  selectionMode="single"
                  onAction={key => {
                    handleRowsPerPageChange(Number(key))
                  }}
                >
                  <DropdownItem key="10">10</DropdownItem>
                  <DropdownItem key="25">25</DropdownItem>
                  <DropdownItem key="100">100</DropdownItem>
                </DropdownMenu>
              </Dropdown>

              {/* Mobile Pagination */}
              <div className="flex items-center gap-2">
                <Button
                  isDisabled={page <= 1}
                  size="sm"
                  variant="flat"
                  onPress={() => handlePageChange(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-[#AFAFAF]">
                  Page {page} of {totalPages}
                </span>
                <Button
                  isDisabled={page >= totalPages}
                  size="sm"
                  variant="flat"
                  onPress={() => handlePageChange(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <TableComponent
          columns={tableColumns}
          currentPage={page}
          data={data}
          disableLocalPagination={true}
          high="h-[calc(100vh-250px)]"
          rowsPerPage={rowsPerPage}
          selectionMode="none"
          showBottomContent={true}
          showTopContent={false}
          sortBy={sortBy}
          sortDirection={sortDirection}
          total={tenantsData?.pageInfo.count || data.length}
          totalPages={totalPages}
          customRenderCell={
            renderCell as unknown as (data: Column, columnKey: Column['uid']) => React.ReactNode
          }
          initialVisibleColumns={[
            'tenantName',
            'type',
            'status',
            'planName',
            'createdAt',
            'actions',
          ]}
          trClassName={(tenant: TenantDto) => {
            const clickable =
              tenant.status === 'active' &&
              (!isSuperAdmin || isDefaultResellerTenant(tenant.resellerId))
            return `border-b dark:border-[#2A2A2A] border-[#2A2A2A]/10 ${clickable ? 'cursor-pointer' : ''} hover:bg-gray-50 dark:hover:bg-gray-800/50`
          }}
          onLimitChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onRowSelect={(tenant: TenantDto) => {
            const clickable =
              tenant.status === 'active' &&
              (!isSuperAdmin || isDefaultResellerTenant(tenant.resellerId))
            if (clickable) {
              handleNavigateToTenantUsers(tenant.id)
            }
          }}
        />
      )}
    </section>
  )
}

export default TenantManagement
